import { Injectable, Inject } from '@angular/core';
import { Request, XHRBackend, RequestOptions } from '@angular/http';
import { Response, Http, RequestOptionsArgs, Headers, ResponseContentType } from '@angular/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/fromPromise';

import { SessionStorageService } from "./session-storage.service";

import { config } from "../../../../config/index";

@Injectable()
export class BaseWebService {

    selectedRole: any;
    defaultRole: any;
    roleCheckFlag: boolean = false;

    constructor(
        private _http: Http,
        private _router: Router,
        private _sessionStorageService: SessionStorageService,
        @Inject('utilService') private utilService: any) {
            this.getRoleDetails();
            this.utilService.defaultRoleChange.subscribe(this.updateRoleDetails);
    }

    updateRoleDetails = (details: any) => {
        if (details.update) {
            this.getRoleDetails();
        }
    }

    getRoleDetails() {
        this._sessionStorageService.readItem(config.role).then((data: any) => {
            if (data) {
                this.selectedRole = data.selectedRole;
                this.defaultRole = data.default_role;
            } else {
                this.selectedRole = null;
                this.defaultRole = null;
            }
            this.roleCheckFlag = true;
        });
    }

    /**
     * @param  {string} url contains url data passed from service
     * @param  {string} httpMethod contains the method used
     * @param  {any={}} data contains data passed with the service
     * @returns Observable
     */
    request = (url: string, httpMethod: string, data: any = {}, selectedRole?: any, defaultRole?: any): Observable<Response> => {
        if (selectedRole && defaultRole && !this.roleCheckFlag) {
            this.selectedRole = selectedRole;
            this.defaultRole = defaultRole;
            this.roleCheckFlag = true;
        }
        return Observable.fromPromise(this.readToken())
            .flatMap(tokenData => this.httpRequester(url, httpMethod, tokenData, data))
            .map(this.extractData)
            .catch(error => this.handleError(error, url));
    }

    requestBlob = (url: string, httpMethod: string, data: any = {}, selectedRole?: any, defaultRole?: any): Observable<Response> => {
        return Observable.fromPromise(this.readToken())
            .flatMap(tokenData => this.httpRequester(url, httpMethod, tokenData, data, true))
            .map(this.extractData)
            .catch(error => this.handleError(error, url));
    }

    requestBlobWithResponseHeader(url: string, httpMethod: string) {
        return Observable.fromPromise(this.readToken())
            .flatMap(tokenData => this.httpRequester(url, httpMethod, tokenData, {}, true))
            .map(res => { return res })
            .catch(error => this.handleError(error, url));
    }

    requestWithUrlToken = (url: string, httpMethod: string, data: any = {}): Observable<Response> => {
        let tokenData = {
            token: data.token
        }
        return this.httpRequester(url, httpMethod, tokenData, data)
            .map(this.extractData)
            .catch((error: any) => this.handleError(error));
    }

    requestWithoutToken = (url: string, httpMethod: string, data: any = {}): Observable<Response> => {
        return this.httpRequester(url, httpMethod, {}, data)
            .map(this.extractData)
            .catch((error: any) => this.handleError(error));
    }

    /**
     * @param  {string} url contains url data passed from service
     * @param  {string} httpMethod contains the method used
     * @param  {any} data data contains data passed with the service
     * @returns Observable
     */
    requestFormData = (url: string, httpMethod: string, data: any): Observable<Response> => {
        return Observable.fromPromise(this.readToken())
            .flatMap(tokenData => this.httpRequesterFormData(url, httpMethod, tokenData, data))
            .map(this.extractData)
            .catch(error => this.handleError(error));
    }

    /**
     * @param  {string} url contains url data passed from service
     * @param  {string} httpMethod contains the method used
     * @param  {any={}} tokenData contains authorization token
     * @param  {any={}} data contains data passed with the service
     */
    private httpRequester = (url: string, httpMethod: string, tokenData: any = {}, data: any = {}, isBlobResponse?: boolean) => {
        httpMethod = httpMethod.toLowerCase();
        tokenData.token = "Token " + tokenData.token;
        if (tokenData.token === "Token undefined") {
            tokenData.token = "undefined";
        }
        let defaultHeaders;
        if (this.selectedRole && this.defaultRole && this.selectedRole.id !== this.defaultRole.id) {
            defaultHeaders = { 'Content-Type': 'application/json', Authorization: tokenData.token, 'USER-ROLE': this.selectedRole.name };
        } else {
            defaultHeaders = { 'Content-Type': 'application/json', Authorization: tokenData.token };            
        }
        const requestHeaders = new Headers({ ...defaultHeaders, ...data.headers });
        const params = [];
        // params.push(config.baseUrl + url);
        params.push({method: httpMethod});
        if (httpMethod !== 'get') params.push({body: data.body});
        if ((url.indexOf('tabular/export')!==-1) || isBlobResponse) {
            params.push({ headers: requestHeaders, responseType: ResponseContentType.Blob});
        } else {
            params.push({ headers: requestHeaders });
        }

        if (url.indexOf('logout') !== -1) {
            sessionStorage.removeItem('logined');
            this._sessionStorageService.clearItems();
        }

        // return this._http[httpMethod](...params);
        return this._http.request(config.baseUrl + url, {...params[0], ...params[1], ...params[2]});
    }

    /**
     * @param  {string} url contains url data passed from service
     * @param  {string} httpMethod contains the method used
     * @param  {any={}} tokenData contains authorization token
     * @param  {any} data contains data passed with the service
     */
    private httpRequesterFormData = (url: string, httpMethod: string, tokenData: any = {}, data: any) => {
        httpMethod = httpMethod.toLowerCase();
        tokenData.token = "Token " + tokenData.token;
        if (tokenData.token === "Token undefined") {
            tokenData.token = "undefined";
        }
        let defaultHeaders;
        if (this.selectedRole && this.defaultRole && this.selectedRole.id !== this.defaultRole.id) {
            defaultHeaders = { Authorization: tokenData.token, 'USER-ROLE': this.selectedRole.name };
        } else {
            defaultHeaders = { Authorization: tokenData.token };
        }
        const requestHeaders = new Headers({ ...defaultHeaders, ...data.headers });
        const params = [];
        params.push(config.baseUrl + url);
        if (httpMethod !== 'get' && httpMethod !== 'delete') params.push(data.body);
        params.push({ headers: requestHeaders });
        return this._http[httpMethod](...params);
    }

    /**
     * This function reads token stored in focalStorage
     */
    private readToken = () => {
        return this._sessionStorageService.readItem(config.storage_key)
            .then(response => {
                if (response === null) response = {};
                return response;
            })
            .catch(this.handleTokenError);
    }

    /**
     * Function to extract response from service call
     * @param  {Response} res
     */
    private extractData = (res: any) => {
        var body;
        try {
            var body = JSON.parse(res._body.replace(/\n/g, "\\n"));
        }
        catch (err) {
            var body = res._body;
        }
        if (res.status === 200 || res.status === 201 || res.status === 204) {
            return body || {};
        }
        else {
            throw body.message;
        }
    }

    /**
     * Function to handle service call errors
     * @param  {Response|any} error
     */
    private handleError = (error: Response | any, url?: any) => {
        let status = error.status;
        const body = error.json();
        let patientApps: any = ['ortho-calculator', 'patient-presenter', 'gp-gallery', '404', 'health-history', 'ses-verification-success', 'ses-verification-failure'];
        if (status === 403) {
            if (body.error_code === 310) {
                this._router.navigate(['home/dashboard'], { queryParams: { verified: false }, skipLocationChange: true });
            } else {
                this._router.navigate(['/logout']);
            }
        } else if ((status === 403 || status === 401)
            && (window.location.href.indexOf('login') === -1)) {
            if (patientApps.includes(window.location.pathname.split('/')[1])) {
                this._sessionStorageService.saveItem('patientAppView', true);
                this._router.navigate(['/404']);
            } else {
                this._router.navigate(['/logout']);
            }
        } else if (status === 402 || status === 312) {
            this.subscriptionErrorHandler(status, body, url);
        }

        throw body;
    }

    private handleTokenError = (error: Response | any) => {
        console.log("token error handle");
    }

    subscriptionErrorHandler(status: any, body: any, url: any) {
        let subscription: any = {};

        subscription.package = status === 402 ? 'PRO' : 'ENTERPRISE';
        if (body.message === 'PAYMENT_PENDING') {
            subscription.pending_payment = {
                type: 'restrict',
            }
        } else if (body.message === 'SUBSCRIPTION_EXPIRED') {
            subscription.subscription_status = 'expired';
            subscription.pending_payment = null;
            subscription.expired_on = body.expired_on;
        }

        this._sessionStorageService.readItem(config.role)
            .then((response: any) => {
                for (var key in subscription) {
                    response[key] = subscription[key];
                }
                this._sessionStorageService.saveItem(config.role, response).then(() => {
                    if (response.role === 'Front Office') {
                        if (this._router.url !== '/patient/patient-file-list') {
                            this._router.routeReuseStrategy.shouldReuseRoute = () => false;
                            this._router.onSameUrlNavigation = 'reload';
                            this._router.navigate(['patient/patient-file-list']);
                        }
                    } else {
                        this.utilService.hideChat.next({ hide: true });
                        this.utilService.hideHeaderNavDropDownNotSubscribed.next({ hide: true });
                        if (url && url.indexOf('/chat/rooms') === -1 && this._router.url !== '/home/dashboard') {
                            this._router.routeReuseStrategy.shouldReuseRoute = () => false;
                            this._router.onSameUrlNavigation = 'reload';
                            this._router.navigate(['home/dashboard']);
                        }
                    }
                });
            });
    }

}